import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, Tag, Plus, FileText } from "lucide-react";
import { toast } from "react-toastify";

import { useTags } from "~/api/swr/useTags";
import { type TagWithDocuments } from "~/api/types";
import { createTags, deleteTag } from "~/api/v2/requests";
import { Spinner } from "~/atoms/spinner";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/atoms/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/atoms/accordion";
import { Input } from "~/atoms/input";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "~/atoms/empty";
import { TagsValue } from "~/molecules/Tags";
import { toComparableString } from "~/helpers/string";

interface TagsModalProps {
  trigger: React.ReactNode;
}

export function TagsModal({ trigger }: TagsModalProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="h-125 w-150 grid-rows-[auto_minmax(0,1fr)]">
        <DialogHeader className="max-h-fit">
          <DialogTitle>{t(($) => $.modal.tags.manageTags)}</DialogTitle>
          <DialogDescription>
            {t(($) => $.commons.tags.manageTagsDescription)}
          </DialogDescription>
        </DialogHeader>
        <TagsModalContent open={open} />
      </DialogContent>
    </Dialog>
  );
}

interface TagsModalContentProps {
  open: boolean;
}

interface CreateTagFormProps {
  newTagName: string;
  tagAlreadyExists: boolean;
  isCreating: boolean;
  onChange: (value: string) => void;
  onCreate: () => Promise<void>;
}

function CreateTagForm({
  newTagName,
  tagAlreadyExists,
  isCreating,
  onChange,
  onCreate,
}: CreateTagFormProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex gap-2">
        <Input
          className="focus-visible:ring-0 focus-visible:ring-offset-0"
          placeholder={t(($) => $.commons.tags.newTagPlaceholder)}
          value={newTagName}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void onCreate();
            }
          }}
        />
        <Button
          className="focus-visible:ring-0 focus-visible:ring-offset-0"
          onClick={onCreate}
          disabled={!newTagName.trim() || tagAlreadyExists || isCreating}
        >
          {isCreating ? (
            <Spinner className="size-4" />
          ) : (
            <Plus className="size-4" />
          )}
          {t(($) => $.commons.buttons.create)}
        </Button>
      </div>
      {tagAlreadyExists && (
        <p className="text-destructive text-sm">
          {t(($) => $.commons.tags.tagExists)}
        </p>
      )}
    </>
  );
}

function NoTagsState() {
  const { t } = useTranslation();

  return (
    <Empty>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Tag />
        </EmptyMedia>
        <EmptyTitle>{t(($) => $.commons.tags.noTags)}</EmptyTitle>
        <EmptyDescription>
          {t(($) => $.commons.tags.noTagsDescription)}
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function TagsModalContent({ open }: TagsModalContentProps) {
  const { t } = useTranslation();
  const { data: tags, isLoading, mutate } = useTags(true);
  const [newTagName, setNewTagName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<TagWithDocuments | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      void mutate();
    }
  }, [open, mutate]);

  const sortedTags = useMemo(() => {
    if (!tags) return [];
    return [...tags].sort((a, b) => a.title.localeCompare(b.title));
  }, [tags]);

  const tagAlreadyExists = useMemo(() => {
    if (!newTagName.trim() || !tags) return false;
    return tags.some(
      (tag) =>
        toComparableString(tag.title) === toComparableString(newTagName.trim()),
    );
  }, [newTagName, tags]);

  const handleCreateTag = async () => {
    if (!newTagName.trim() || tagAlreadyExists) return;

    setIsCreating(true);
    try {
      await createTags([newTagName.trim()]);
      toast.success(t(($) => $.commons.tags.createSuccess));
      void mutate();
      setNewTagName("");
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    }
    setIsCreating(false);
  };

  const handleDeleteClick = (tag: TagWithDocuments) => {
    setTagToDelete(tag);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!tagToDelete) return;

    setIsDeleting(true);
    await deleteTag(tagToDelete.id);
    toast.success(t(($) => $.commons.tags.deleteSuccess));

    void mutate();
    setDeleteDialogOpen(false);
    setTagToDelete(null);
    setIsDeleting(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 overflow-hidden">
      <CreateTagForm
        newTagName={newTagName}
        tagAlreadyExists={tagAlreadyExists}
        isCreating={isCreating}
        onChange={setNewTagName}
        onCreate={handleCreateTag}
      />
      {!tags || tags.length === 0 ? (
        <NoTagsState />
      ) : (
        <Accordion
          type="multiple"
          className="flex flex-col gap-1 overflow-y-auto"
        >
          {sortedTags.map((tag) => {
            const hasDocuments = tag.documents && tag.documents.length > 0;

            return (
              <AccordionItem
                key={tag.id}
                value={String(tag.id)}
                className="border-none"
              >
                {hasDocuments ? (
                  <div className="hover:bg-muted grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-md p-2">
                    <AccordionTrigger className="cursor-pointer flex-row-reverse items-center justify-end gap-2 p-0 hover:no-underline [&>svg]:size-5">
                      <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_11rem] items-center gap-4">
                        <TagsValue>{tag.title}</TagsValue>
                        <span className="text-muted-foreground text-right text-sm font-normal">
                          {t(($) => $.commons.tags.usedInXDocuments, {
                            count: tag.documents!.length,
                          })}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <Button
                      variant="ghost"
                      className="[&>svg]:text-muted-foreground hover:bg-destructive/10 hover:[&>svg]:text-destructive rounded-md p-2 [&>svg]:size-5"
                      onClick={() => handleDeleteClick(tag)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-[minmax(0,1fr)_11rem_auto] items-center gap-4 p-2">
                    <div className="min-w-0 pl-8">
                      <TagsValue>{tag.title}</TagsValue>
                    </div>
                    <span className="text-muted-foreground text-right text-sm">
                      {t(($) => $.commons.tags.notUsed)}
                    </span>
                    <Button
                      variant="ghost"
                      className="[&>svg]:text-muted-foreground hover:bg-destructive/10 hover:[&>svg]:text-destructive rounded-md p-2 [&>svg]:size-5"
                      onClick={() => handleDeleteClick(tag)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                )}

                {hasDocuments && (
                  <AccordionContent className="pl-8">
                    <div className="border-border space-y-1 border-l-2 py-2 pl-5">
                      {tag.documents!.map((doc) => (
                        <div
                          key={doc.id}
                          className="text-muted-foreground flex items-center gap-3 px-3 py-2 text-sm"
                        >
                          <FileText className="size-4 shrink-0" />
                          <span>
                            {doc.documentVersions[0]?.title ?? "Untitled"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                )}
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <AlertDialog
        open={deleteDialogOpen || isDeleting}
        onOpenChange={setDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(($) => $.commons.tags.deleteConfirmTitle)}
            </AlertDialogTitle>
            <div className="flex items-center gap-2 py-2">
              <TagsValue>{tagToDelete?.title}</TagsValue>
            </div>
            <AlertDialogDescription>
              {tagToDelete &&
                t(($) => $.commons.tags.deleteConfirmDescription, {
                  count: tagToDelete.documents?.length ?? 0,
                })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t(($) => $.commons.buttons.cancel)}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              onClick={handleConfirmDelete}
              variant="destructive"
            >
              {isDeleting ? (
                <>
                  <Spinner className="size-4" />
                  {t(($) => $.commons.loading)}
                </>
              ) : (
                t(($) => $.commons.buttons.delete)
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
